import {
    AfterContentInit, Component, ContentChild, HostBinding, Inject,
    Input, OnDestroy, SkipSelf, TemplateRef,
} from "@angular/core";
import { BooleanAttribute } from "@hex/angular/decorators";
import { get as _get, isEmpty, isEqual } from "lodash";
import { Subscription } from "rxjs";
import { GenericActions } from "../../../redux/generic-actions";
import { ImmutableRedux } from "../../../redux/immutable-redux.service";
import { GenericFormActions } from "../actions";
import { CommonFormManagerDirective } from "../form-manager.directive";
import { FORM_FIELD_GROUP_STATE_PROP } from "../state-props";
import { fieldGroupListReducerFactory } from "./reducer";

@Component({
    selector: "common-field-group-list",
    templateUrl: "./field-group-list.component.html",
    exportAs: "CommonFieldGroupList",
})
export class CommonFieldGroupListComponent implements OnDestroy, AfterContentInit {
    @HostBinding("class") public classes = "u-hex-width-full";
    @Input() public fieldProp: string;
    @Input() public fieldLabel: string;
    @Input() public addButtonText = "Add";
    @Input() public addButtonLabel: string;
    @Input() public groupTypeDropdownOptions: any;
    @Input() @BooleanAttribute() public fieldLabelIsSectionHeader;
    @Input() @BooleanAttribute() public staticFieldGroupList: boolean;
    @Input() @BooleanAttribute() public inlineAddButton: boolean;
    @Input() public displayRemoveButtonAtIndex = 1;
    @Input("initializeGroupsOnEdit") public initializeGroupsOnEditInput = true;

    @Input("numOfStartingGroups")
    public set numOfStartingGroupsInput(value: number) {
        this._numOfStartingGroupsInput = value;
        this.initializeStartingGroups();
    }

    public get numOfStartingGroups(): number {
        return this._numOfStartingGroupsInput;
    }

    private _numOfStartingGroupsInput = 1;

    @ContentChild(TemplateRef,  {static: false}) public templateRef: TemplateRef<any>;

    public state$: Subscription;
    public formState$: Subscription;
    public typeOfGroupToAddNext: any;
    public groups: any[] = [];

    constructor(
        public ngRedux: ImmutableRedux<any>,
        @SkipSelf() public formManager: CommonFormManagerDirective,
        @Inject(FORM_FIELD_GROUP_STATE_PROP) public rootProp,
    ) { }

    public ngAfterContentInit(): void {
        this.initializeStartingGroups();
        this.formManager.registerFieldGroupList(this);

        this.ngRedux.registerComponent(
            fieldGroupListReducerFactory,
            this.rootProp,
            this.stateProp,
            { groups: this.groups },
        );
        this.subscribeToStateChanges();
        this.setupReplaySubject();
    }

    public ngOnDestroy(): void {
        this.state$.unsubscribe();
        this.formManager.unregisterFieldGroupList(this);
        this.ngRedux.dispatchFactory(GenericActions.STATE_RESET, this.rootProp, this.stateProp);
    }

    public loadGroups(existingGroups): void {
        this.ngRedux.dispatchFactory(GenericFormActions.GROUPS_LOADED, this.rootProp, this.stateProp, existingGroups);
    }

    public fieldPropPrefix(index: number): string {
        return `${this.fieldProp}::${index}::`;
    }

    public get stateProp(): string {
        return `${this.formManager.stateProp}::${this.rootProp}::${this.fieldProp}`;
    }

    public displayAddButton(): boolean {
        if (!!this.groupTypeDropdownOptions) {
            return !!this.typeOfGroupToAddNext;
        } else {
            return true;
        }
    }

    public displayRemoveButton(index): boolean {
        return index >= this.displayRemoveButtonAtIndex;
    }

    public groupTrackBy(index: number, item): number {
        return index;
    }

    public lastItem(index: number): boolean {
        return index === (this.groups.length - 1);
    }

    public get dynamicFieldGroupList(): boolean {
        return !this.staticFieldGroupList;
    }

    public emptyGroupObject(index): { groupId: number } {
        return { groupId: index + 1 };
    }

    public get initializeGroupsOnEdit(): boolean {
        return this.formManager.isEditForm && this.initializeGroupsOnEditInput && this.numOfStartingGroups > 0;
    }

    public get initializeGroupsOnNew(): boolean {
        return !this.formManager.isEditForm && this.numOfStartingGroups > 0;
    }

    public initializeStartingGroups(): void {
        if (this.initializeGroupsOnEdit || this.initializeGroupsOnNew) {
            this.groups = new Array(this.numOfStartingGroups).fill({}).map((group, index) => this.emptyGroupObject(index));
        }
    }

    public subscribeToStateChanges(): void {
        this.state$ = this.ngRedux.select(this.rootProp, this.stateProp).subscribe((state) => {
            if (!isEqual(state.groups, this.groups)) {
                this.groups = state.groups;
            }
        });
    }

    public setupReplaySubject(): void {
        this.formState$ = this.formManager.stateReplaySubject$.subscribe((formState) => {
            const data = _get(formState, ["formData", ...this.fieldProp.split("::")]);
            if ((!!data && !!this.groups) &&
                (data.length > this.groups.length) &&
                (this.groups.every((group) => !group.isManuallyAddedGroup)) &&
                (data.every((group) => !isEmpty(group)))
            ) {
                this.loadGroups(data.map((group, index) => this.emptyGroupObject(index)));
            }
        });
    }
}
